/**
 * Weather Legend Toggle Button
 * Shows in the layer control for DTN weather layers that have a legend
 */

import React from 'react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiListBullets } from 'react-icons/pi';
import { LayerItem } from '@ais-ui/react-openlayers';
import { useWeatherLegend } from '../WeatherLegend';
import { cn } from '@ais-ui/utils';

interface WeatherLegendToggleProps {
  layer: LayerItem;
}

export const WeatherLegendToggle: React.FC<WeatherLegendToggleProps> = ({ layer }) => {
  const { toggleLegend, isLegendActiveForLayer } = useWeatherLegend();
  
  // Extract the DTN layer ID and hasLegend from the layer properties
  const dtnLayerId = layer.properties?.dtnLayerId || layer.id.replace('dtn-weather-', '');
  const hasLegend = layer.properties?.hasLegend === true;
  const isActive = isLegendActiveForLayer(dtnLayerId);

  const handleClick = () => {
    toggleLegend(dtnLayerId, layer.name);
  };

  // Don't render if no legend available (stored at layer creation time)
  if (!hasLegend) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id={`legend-toggle-${layer.id}`}
          size="icon"
          variant="ghost"
          className={cn(
            'h-9 w-9 flex-shrink-0 p-0 m-0',
            isActive && 'bg-accent'
          )}
          onClick={handleClick}
        >
          <PiListBullets className="size-5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isActive ? 'Hide legend' : 'Show legend'}
      </TooltipContent>
    </Tooltip>
  );
};

export default WeatherLegendToggle;
